/**
 * DialogProvider — fornece os hooks `useConfirm`, `usePrompt`, `useToast`.
 *
 * Substitui window.confirm/window.alert/window.prompt nativos.
 * Renderiza modal único compartilhado + stack de toasts top-right.
 *
 * Uso:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, description, danger });
 *
 *   const toast = useToast();
 *   toast.success("Salvo!");
 *
 *   const prompt = usePrompt();
 *   const url = await prompt({ title, label, placeholder });
 */
"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Modal, ModalBtnCancel, ModalBtnPrimary } from "./Modal";


// ════════════════════════════════════════════════════════════════════════
// Tipos
// ════════════════════════════════════════════════════════════════════════
interface ConfirmOptions {
  title:        string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?:  string;
  danger?:      boolean;
}

interface PromptOptions {
  title:         string;
  label?:        string;
  placeholder?:  string;
  defaultValue?: string;
  confirmLabel?: string;
  multiline?:    boolean;
}

type ToastVariant = "success" | "error" | "info";
interface ToastItem {
  id:       string;
  variant:  ToastVariant;
  message:  string;
  expiresAt: number;
}

interface DialogContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt:  (opts: PromptOptions)  => Promise<string | null>;
  toast: {
    success: (msg: string) => void;
    error:   (msg: string) => void;
    info:    (msg: string) => void;
  };
}


const DialogContext = createContext<DialogContextValue | null>(null);


// ════════════════════════════════════════════════════════════════════════
// Provider
// ════════════════════════════════════════════════════════════════════════
export function DialogProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  // Confirm state
  const [confirmState, setConfirmState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  // Prompt state
  const [promptState, setPromptState]   = useState<(PromptOptions & { resolve: (v: string | null) => void }) | null>(null);
  const [promptValue, setPromptValue]   = useState("");
  // Toasts
  const [toasts, setToasts]             = useState<ToastItem[]>([]);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      setConfirmState({ ...opts, resolve });
    });
  }, []);

  const prompt = useCallback((opts: PromptOptions): Promise<string | null> => {
    return new Promise(resolve => {
      setPromptValue(opts.defaultValue || "");
      setPromptState({ ...opts, resolve });
    });
  }, []);

  // Toasts auto-dismiss
  useEffect(() => {
    if (toasts.length === 0) return;
    const t = setInterval(() => {
      const now = Date.now();
      setToasts(prev => prev.filter(x => x.expiresAt > now));
    }, 250);
    return () => clearInterval(t);
  }, [toasts.length]);

  const showToast = useCallback((variant: ToastVariant, message: string) => {
    const id = Math.random().toString(36).slice(2, 9);
    const expiresAt = Date.now() + 4000;
    setToasts(prev => [...prev.slice(-2), { id, variant, message, expiresAt }]);  // max 3
  }, []);

  const toast = {
    success: useCallback((msg: string) => showToast("success", msg), [showToast]),
    error:   useCallback((msg: string) => showToast("error",   msg), [showToast]),
    info:    useCallback((msg: string) => showToast("info",    msg), [showToast]),
  };

  // Confirm handlers
  const closeConfirm = (result: boolean) => {
    if (!confirmState) return;
    confirmState.resolve(result);
    setConfirmState(null);
  };

  // Prompt handlers
  const closePrompt = (result: string | null) => {
    if (!promptState) return;
    promptState.resolve(result);
    setPromptState(null);
    setPromptValue("");
  };

  return (
    <DialogContext.Provider value={{ confirm, prompt, toast }}>
      {children}

      {/* Confirm Modal */}
      {confirmState && (
        <Modal
          open
          onCancel={() => closeConfirm(false)}
          title={confirmState.title}
          width={460}
          footer={
            <>
              <ModalBtnCancel onClick={() => closeConfirm(false)}>
                {confirmState.cancelLabel || t("dialog.cancel")}
              </ModalBtnCancel>
              <ModalBtnPrimary danger={confirmState.danger} onClick={() => closeConfirm(true)}>
                {confirmState.confirmLabel || t("dialog.confirm")}
              </ModalBtnPrimary>
            </>
          }
        >
          {confirmState.description && (
            <div className="text-sm text-app-2 leading-relaxed pb-3 whitespace-pre-line">
              {confirmState.description}
            </div>
          )}
        </Modal>
      )}

      {/* Prompt Modal */}
      {promptState && (
        <Modal
          open
          onCancel={() => closePrompt(null)}
          title={promptState.title}
          width={500}
          footer={
            <>
              <ModalBtnCancel onClick={() => closePrompt(null)}>Cancelar</ModalBtnCancel>
              <ModalBtnPrimary
                onClick={() => closePrompt(promptValue.trim())}
                disabled={!promptValue.trim()}
              >
                {promptState.confirmLabel || t("dialog.confirm")}
              </ModalBtnPrimary>
            </>
          }
        >
          <div className="pb-3">
            {promptState.label && (
              <label className="block text-xs font-semibold text-app-3 uppercase tracking-wide mb-1.5">
                {promptState.label}
              </label>
            )}
            {promptState.multiline ? (
              <textarea
                value={promptValue}
                onChange={e => setPromptValue(e.target.value)}
                placeholder={promptState.placeholder}
                rows={4}
                autoFocus
                className="w-full rounded-lg border border-app px-3 py-2 text-sm focus:outline-none focus:border-slate-400 resize-none"
              />
            ) : (
              <input
                type="text"
                value={promptValue}
                onChange={e => setPromptValue(e.target.value)}
                placeholder={promptState.placeholder}
                autoFocus
                onKeyDown={e => {
                  if (e.key === "Enter" && promptValue.trim()) {
                    closePrompt(promptValue.trim());
                  }
                }}
                className="w-full rounded-lg border border-app px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
              />
            )}
          </div>
        </Modal>
      )}

      {/* Toasts */}
      <ToastStack toasts={toasts} onDismiss={(id) => setToasts(prev => prev.filter(x => x.id !== id))} />
    </DialogContext.Provider>
  );
}


// ════════════════════════════════════════════════════════════════════════
// Toast UI
// ════════════════════════════════════════════════════════════════════════
function ToastStack({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;

  return createPortal(
    <div className="fixed top-20 right-4 z-[400] flex flex-col gap-2 pointer-events-none">
      {toasts.map(t => (
        <ToastCard key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}


function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const variants: Record<ToastVariant, { bg: string; border: string; text: string; icon: React.ReactElement }> = {
    success: {
      bg:     "bg-app-surface",
      border: "border-emerald-200",
      text:   "text-app-1",
      icon:   <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>,
    },
    error: {
      bg:     "bg-app-surface",
      border: "border-rose-200",
      text:   "text-app-1",
      icon:   <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-rose-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
              </svg>,
    },
    info: {
      bg:     "bg-app-surface",
      border: "border-app",
      text:   "text-app-1",
      icon:   <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-app-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
              </svg>,
    },
  };
  const v = variants[item.variant];

  return (
    <div
      onClick={onDismiss}
      className={`pointer-events-auto cursor-pointer ${v.bg} ${v.border} ${v.text} rounded-xl shadow-lg border px-4 py-3 min-w-[280px] max-w-[400px] flex items-start gap-3 animate-toast-in`}
      style={{ animationDuration: "200ms" }}
    >
      {v.icon}
      <p className="text-sm font-medium leading-relaxed pr-2 flex-1">{item.message}</p>
      <style jsx>{`
        @keyframes toast-in-kf {
          from { opacity: 0; transform: translateX(20px) }
          to   { opacity: 1; transform: translateX(0) }
        }
        :global(.animate-toast-in) { animation: toast-in-kf 200ms cubic-bezier(.4,0,.2,1); }
      `}</style>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// Hooks
// ════════════════════════════════════════════════════════════════════════
export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog precisa estar dentro de <DialogProvider>");
  }
  return ctx;
}

export function useConfirm() { return useDialog().confirm; }
export function usePrompt()  { return useDialog().prompt; }
export function useToast()   { return useDialog().toast; }
